import { useState } from "react";

import { useTranslate } from "@similarweb/i18n-provider";

import { useDidUpdate } from "app/custom-hooks/useDidUpdate";
import { StyledManagementCardButton } from "app/pages/sales-intelligence/modules/manage-integrations/components/cards-components/styles";
import { useCRMIntegrationStatusQuery } from "app/pages/sales-intelligence/modules/manage-integrations/query/queries/useCRMIntegrationStatusQuery";
import { DynamicsConnectModal } from "app/pages/sales-intelligence/pages/management/components/ManagementSections/components/Modals/DynamicsConnectModal/DynamicsConnectModal";
import TermsModal from "app/pages/sales-intelligence/pages/management/components/ManagementSections/components/Modals/TermsModal";
import {
    INTEGRATION_STATUS,
    LOGIN_HEIGHT,
    LOGIN_LINKS,
    LOGIN_WIDTH,
    RECONNECT_LINKS,
    SYNC_FAILED_REASON_CODE_INSUFFICIENT_PERMISSIONS,
} from "app/pages/sales-intelligence/pages/management/constants/constants";
import {
    MANAGEMENT_COMMON_CONNECT,
    MANAGEMENT_COMMON_MANAGE,
    MANAGEMENT_COMMON_RECONNECT,
} from "app/pages/sales-intelligence/pages/management/translations/managementKeys";
import type { Integration } from "app/pages/sales-intelligence/sub-modules/management/types";
import type { Timer } from "app/pages/sales-intelligence/types";
import siNavigator from "app/pages/sales-intelligence/services/siNavigator";
import { ExportIntegrations } from "app/pages/sales-intelligence/pages/management/types/types";

interface IProps {
    integrationName: Integration;
    connectButtonText?: string;
    onConnectClick?: VoidFunction;
    onConnect?: VoidFunction;
    onManage?: VoidFunction;
    onApproveTerms?: VoidFunction;
    onDenyTerms?: VoidFunction;
}

const CRMConnectButton = ({
    integrationName,
    connectButtonText,
    onConnectClick,
    onConnect,
    onManage,
    onApproveTerms,
    onDenyTerms,
}: IProps): React.ReactElement => {
    const { translate } = useTranslate();

    const crmStatusQuery = useCRMIntegrationStatusQuery(integrationName);

    const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
    const [connectedWindow, setConnectedWindow] = useState<Window | null>(null);

    const isDynamics = integrationName === ExportIntegrations.dynamics;

    const onRefetch = () => {
        void crmStatusQuery.refetch();
    };

    useDidUpdate(() => {
        let timer: Timer;
        if (connectedWindow) {
            timer = setInterval(() => {
                if (connectedWindow.closed) {
                    onRefetch();
                    clearInterval(timer);
                }
            }, 1500);
        }

        return () => {
            clearInterval(timer);
            setConnectedWindow(null);
        };
    }, [connectedWindow]);

    if (crmStatusQuery.isLoading) {
        return (
            <StyledManagementCardButton
                isDisabled
                buttonHtmlType="button"
                type="primary"
                data-automation="crm-connect-button-loading"
            >
                {translate(MANAGEMENT_COMMON_CONNECT)}
            </StyledManagementCardButton>
        );
    }

    const { crmConnectionStatus } = crmStatusQuery.data;
    const status = crmConnectionStatus.syncStatus;
    const syncFailedReasonCode = crmConnectionStatus.syncFailedReasonCode;
    const isIntegrated = status !== INTEGRATION_STATUS.NotIntegrated;
    const isFailed =
        status === INTEGRATION_STATUS.Failed &&
        syncFailedReasonCode === SYNC_FAILED_REASON_CODE_INSUFFICIENT_PERMISSIONS;

    const handleOpenWindow = (url: string) => {
        const left = (screen.width - LOGIN_WIDTH) / 2;
        const top = (screen.height - LOGIN_HEIGHT) / 4;
        const connected = window.open(
            url,
            "_blank",
            `location=0,status=0,width=600px,height=650px,resizable=yes,left=${left},top=${top}`,
        );
        setConnectedWindow(connected);
    };

    const handleReconnect = () => {
        handleOpenWindow(RECONNECT_LINKS[integrationName]);
    };

    const handleConfirmTermsModal = () => {
        onApproveTerms?.();
        handleOpenWindow(LOGIN_LINKS[integrationName]);
        setIsConfirmationModalOpen(false);
    };

    const handleCloseTermsModal = () => {
        onDenyTerms?.();
        setIsConfirmationModalOpen(false);
    };

    const handleConfirmDynamicsModal = (environmentUrl: string) => {
        onApproveTerms?.();
        handleOpenWindow(`${LOGIN_LINKS[integrationName]}?environmentUrl=${encodeURIComponent(environmentUrl)}`);
        setIsConfirmationModalOpen(false);
    };

    const handleCloseDynamicsModal = () => {
        onDenyTerms?.();
        setIsConfirmationModalOpen(false);
    };

    const handleClick = () => {
        if (isFailed) {
            onConnect?.();
            handleReconnect();
            return;
        }

        if (isIntegrated) {
            onManage?.();
            void siNavigator.goToCrmManagementPage({ crm: integrationName });
            return;
        }

        if (onConnectClick) {
            onConnect?.();
            onConnectClick();

            return;
        }

        onConnect?.();
        setIsConfirmationModalOpen(true);
    };

    const buttonTextKey = isFailed
        ? MANAGEMENT_COMMON_RECONNECT
        : isIntegrated
          ? MANAGEMENT_COMMON_MANAGE
          : (connectButtonText ?? MANAGEMENT_COMMON_CONNECT);

    return (
        <>
            <StyledManagementCardButton
                buttonHtmlType="button"
                type={isIntegrated ? "outlined" : "primary"}
                onClick={handleClick}
                data-automation="crm-connect-button"
            >
                {translate(buttonTextKey)}
            </StyledManagementCardButton>

            {isDynamics ? (
                <DynamicsConnectModal
                    isOpen={isConfirmationModalOpen}
                    onConfirm={handleConfirmDynamicsModal}
                    onClose={handleCloseDynamicsModal}
                />
            ) : (
                <TermsModal
                    integrationName={integrationName}
                    onConfirm={handleConfirmTermsModal}
                    onClose={handleCloseTermsModal}
                    isOpen={isConfirmationModalOpen}
                />
            )}
        </>
    );
};

export default CRMConnectButton;
